import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { useDocumentHead } from '@/hooks/use-document-head'
import { useHeaderExtra } from '@/contexts/header.context'
import { evalApiClient } from '@/lib/api/eval.client'
import { EvalRunDetailView } from '@/components/eval/EvalRunDetailView'
import { Skeleton } from '@/components/ui/skeleton'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import type { EvalRun } from '@super/shared-types'
import { EvalRunStatus } from '@super/shared-types'

const POLL_INTERVAL_MS = 3_000
const TERMINAL_STATUSES: EvalRunStatus[] = [
  EvalRunStatus.COMPLETED,
  EvalRunStatus.FAILED,
]

function formatRunDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function EvalRunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [run, setRun] = useState<EvalRun | null>(null)

  useDocumentHead({
    title: run ? `Run · ${formatRunDate(run.createdAt)}` : 'Run Detail',
  })

  useHeaderExtra(
    <div className="flex items-center gap-1.5 text-sm">
      <Link
        to="/evaluations?tab=runs"
        className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent transition-colors"
      >
        <ArrowLeft className="w-4 h-4 text-muted-foreground" />
      </Link>
      <Link
        to="/evaluations?tab=runs"
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        Runs
      </Link>
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
      <span className="text-foreground">
        {run ? formatRunDate(run.createdAt) : '…'}
      </span>
    </div>,
    [run?.createdAt],
  )

  useEffect(() => {
    if (!id) return
    evalApiClient.getRunById(id).then(r => {
      if (r) setRun(r)
    })
  }, [id])

  // Poll while run is active
  useEffect(() => {
    if (!id || !run) return
    if (TERMINAL_STATUSES.includes(run.status as EvalRunStatus)) return

    const interval = setInterval(async () => {
      const updated = await evalApiClient.getRunById(id)
      if (updated) {
        setRun(updated)
        if (TERMINAL_STATUSES.includes(updated.status as EvalRunStatus)) {
          clearInterval(interval)
        }
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [id, run?.status])

  return (
    <div className="h-full overflow-y-auto pt-16">
      <FloatingActionIslandProvider>
        <div className="p-6 pb-24">
          {!run ? (
            <div className="flex flex-col gap-4 max-w-3xl">
              <div className="grid grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-6 w-16" />
                  </div>
                ))}
              </div>
              <div className="rounded-md border">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-3 border-b last:border-0"
                  >
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-12 ml-auto" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EvalRunDetailView run={run} />
          )}
        </div>
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}
